import { Component, ElementRef, HostListener, Input, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { fade } from '@core/animations/fade.animation';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { MatCheckbox } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { chain } from 'lodash';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface OutlinedSelectOption {
  text: string;
  value: unknown;
}

@Component({
  selector: 'fc-outlined-multi-select',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './fc-outlined-multi-select.component.html',
  styleUrls: ['./fc-outlined-multi-select.component.scss'],
  animations: [fade]
})
export class FcOutlinedMultiSelectComponent {
  @Input()
  control: FormArray;

  @Input()
  required: boolean;

  @Input()
  label: string;

  @Input()
  options: OutlinedSelectOption[];

  @Input()
  public showAllOption: boolean;

  public showOptions: boolean;
  public readonly allOption: OutlinedSelectOption;

  constructor(
    private elementRef: ElementRef,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.showOptions = false;
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.allOption = {
      text: this.translateService.instant('FC_OUTLINED_MULTI_SELECT.ALL_OPTION'),
      value: { isAllOption: true }
    };
  }

  @HostListener('document:click', ['$event'])
  public onClickOutside(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showOptions = false;
    }
  }

  public displayWith(value: OutlinedSelectOption[]): string {
    return chain(value).map('text').sortBy().value().join(', ');
  }

  private findIndexOfControl(option: OutlinedSelectOption): number {
    return this.control.controls.findIndex((control: FormControl) => control.value.text === option.text);
  }

  public isSelected(option: OutlinedSelectOption): boolean {
    return this.findIndexOfControl(option) >= 0;
  }

  public selectOption(option: OutlinedSelectOption, checkbox: MatCheckbox): void {
    checkbox.checked = !checkbox.checked;
    const checked = checkbox.checked;
    this.updateFormArray({ option, checked, isAll: false });
  }

  public selectAll(allOptionsCheckbox: MatCheckbox): void {
    allOptionsCheckbox.checked = !allOptionsCheckbox.checked;
    this.updateFormArray({ option: this.allOption, checked: allOptionsCheckbox.checked, isAll: true });
  }

  private updateFormArray({ option, checked, isAll }): void {
    if (!isAll) {
      this.removeOptionControl(this.allOption);
    }

    if (checked) {
      const optionControl = this.formBuilder.group({
        text: option.text,
        value: option.value
      });

      if (isAll) {
        this.control.clear();
      }

      this.control.push(optionControl);
      return;
    }

    this.removeOptionControl(option);
  }

  private removeOptionControl(option: OutlinedSelectOption): void {
    if (this.isSelected(option)) {
      const indexOfControl = this.findIndexOfControl(option);
      this.control.removeAt(indexOfControl);
    }
  }
}
